/**
 * Nexus Agency Scope Types
 *
 * The scope determines what data the user is viewing and what actions apply to.
 * This is critical for preventing wrong-client actions and ensuring clear context.
 *
 * Scope states:
 * - all_clients: Platform level, viewing across all clients (e.g., /actions, /home)
 * - client_scoped: Viewing a specific client (e.g., /w/:clientSlug/* or /actions?client=slug)
 * - campaign_scoped: Viewing a specific campaign within a client (future)
 *
 * Scope sources:
 * - route: Scope derived from URL path (/w/:clientSlug)
 * - query: Scope derived from query param (?client=slug)
 *
 * Precedence rule: route always wins over query param
 */

// =============================================================================
// Scope Types
// =============================================================================

export type ScopeLevel = "all_clients" | "client_scoped" | "campaign_scoped";

/** Where the scope was derived from */
export type ScopeSource = "route" | "query" | "none";

export interface Scope {
  level: ScopeLevel;
  /** Where this scope was derived from */
  source: ScopeSource;
  clientId?: string;
  clientName?: string;
  clientSlug?: string;
  campaignId?: string;
  campaignName?: string;
}

// =============================================================================
// Scope Display Helpers
// =============================================================================

/**
 * Get display label for a scope
 */
export function getScopeLabel(scope: Scope): string {
  switch (scope.level) {
    case "all_clients":
      return "All Clients";
    case "client_scoped":
      return scope.clientName || "Client";
    case "campaign_scoped":
      return scope.campaignName
        ? `${scope.clientName}: ${scope.campaignName}`
        : scope.clientName || "Campaign";
  }
}

/**
 * Get short label for scope chip
 */
export function getScopeShortLabel(scope: Scope): string {
  switch (scope.level) {
    case "all_clients":
      return "All Clients";
    case "client_scoped":
      return scope.clientName || "Client";
    case "campaign_scoped":
      return scope.campaignName || "Campaign";
  }
}

/**
 * Get scope icon name for visual indicator
 */
export function getScopeIcon(
  scope: Scope,
): "building2" | "folder" | "megaphone" {
  switch (scope.level) {
    case "all_clients":
      return "building2";
    case "client_scoped":
      return "folder";
    case "campaign_scoped":
      return "megaphone";
  }
}

// =============================================================================
// Scope Factory Functions
// =============================================================================

/**
 * Create an "all clients" scope (platform level)
 */
export function createAllClientsScope(): Scope {
  return { level: "all_clients", source: "none" };
}

/**
 * Create a client-scoped scope from route path (/w/:clientSlug)
 */
export function createClientScopeFromRoute(
  clientId: string,
  clientName: string,
  clientSlug: string,
): Scope {
  return {
    level: "client_scoped",
    source: "route",
    clientId,
    clientName,
    clientSlug,
  };
}

/**
 * Create a client-scoped scope from query param (?client=slug)
 */
export function createClientScopeFromQuery(
  clientId: string,
  clientName: string,
  clientSlug: string,
): Scope {
  return {
    level: "client_scoped",
    source: "query",
    clientId,
    clientName,
    clientSlug,
  };
}

/**
 * Create a client-scoped scope (legacy helper - defaults to route source)
 * @deprecated Use createClientScopeFromRoute or createClientScopeFromQuery
 */
export function createClientScope(clientId: string, clientName: string): Scope {
  return {
    level: "client_scoped",
    source: "route",
    clientId,
    clientName,
  };
}

/**
 * Create a campaign-scoped scope
 */
export function createCampaignScope(
  clientId: string,
  clientName: string,
  campaignId: string,
  campaignName: string,
): Scope {
  return {
    level: "campaign_scoped",
    source: "route",
    clientId,
    clientName,
    campaignId,
    campaignName,
  };
}

// =============================================================================
// Scope Confirmation Helpers
// =============================================================================

/**
 * Get confirmation message for destructive actions
 * Per Standard B: destructive actions must include client name in confirmation
 */
export function getDestructiveActionConfirmation(
  scope: Scope,
  actionDescription: string,
): string {
  switch (scope.level) {
    case "all_clients":
      return `Are you sure you want to ${actionDescription}? This action cannot be undone.`;
    case "client_scoped":
      return `Are you sure you want to ${actionDescription} for ${scope.clientName}? This action cannot be undone.`;
    case "campaign_scoped":
      return `Are you sure you want to ${actionDescription} for ${scope.campaignName} (${scope.clientName})? This action cannot be undone.`;
  }
}

/**
 * Get entity name for use in confirmation dialogs
 */
export function getScopeEntityName(scope: Scope): string {
  switch (scope.level) {
    case "all_clients":
      return "all clients";
    case "client_scoped":
      return scope.clientName || "this client";
    case "campaign_scoped":
      return scope.campaignName || "this campaign";
  }
}
